import { FC, SVGProps, useCallback, useEffect, useMemo, useRef, useState } from "react"
import { addDays, endOfMonth, format, parseISO } from "date-fns"
import { ChartData, ChartOptions } from "chart.js"
import { useNavigate } from "react-router-dom"
import {
    applyDrilldown,
    formatExtendedFieldsSince,
    formatLoyaltyMoney,
    formatLoyaltyNumber,
    getLoyaltyAnalyticsErrorMessage,
    isLoyaltyAnalyticsPeriodTooLong,
    MAX_PERIOD_DAYS,
    resetFilters,
    selectLoyaltyAnalyticsFilters,
    selectLoyaltyStatisticsAppliedFiltersCount,
    setIncludeUnknownLevel,
    setLevels,
    setPeriod,
} from "@/features/loyalty-analytics"
import {
    getLoyaltyLevelBreakdown,
    getLoyaltySummary,
    getLoyaltyTimeSeries,
    ILoyaltyLevelBreakdownResponse,
    ILoyaltySummaryResponse,
    ILoyaltyTimeSeriesResponse,
} from "@/shared/api/loyalty-analytics"
import BagSvg from "@/shared/assets/images/Bag.svg"
import CoinsSvg from "@/shared/assets/images/Coins.svg"
import MoneySvg from "@/shared/assets/images/Money.svg"
import PercentSvg from "@/shared/assets/images/Percent.svg"
import UserListSvg from "@/shared/assets/images/UserList.svg"
import UsersFourSvg from "@/shared/assets/images/UsersFour.svg"
import WalletSvg from "@/shared/assets/images/Wallet.svg"
import { useAppDispatch, useAppSelector } from "@/shared/lib/redux"
import { ChartBlock } from "@/shared/ui/chart-block"
import { Loader } from "@/shared/ui/loader"
import { NoTableData } from "@/shared/ui/no-table-data"
import { getPreviousPeriod, PeriodPicker, PeriodValue } from "@/shared/ui/period-picker"
import { Select } from "@/shared/ui/select"
import { StatisticsBlock } from "@/shared/ui/statistics-block"
import { PageHeader } from "@/widgets/page-header"

const ALL_OPTION_VALUE = "__all__"
const UNKNOWN_LEVEL_OPTION_VALUE = "__unknown_level__"
type LoadStatus = "idle" | "loading" | "loaded" | "error"

const getDelta = (
    current: number | string | null,
    previous: number | string | null | undefined
): { label: string; className: string } => {
    if (current === null || previous === null || typeof previous === "undefined") {
        return { label: "н/д", className: "text-[#828282]" }
    }
    const currentValue = Number(current)
    const previousValue = Number(previous)
    if (!Number.isFinite(currentValue) || !Number.isFinite(previousValue) || previousValue === 0) {
        return { label: "н/д", className: "text-[#828282]" }
    }

    const delta = ((currentValue - previousValue) / Math.abs(previousValue)) * 100
    const value = new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 1 }).format(Math.abs(delta))
    if (delta > 0) return { label: `↑ ${value} %`, className: "text-color-primary" }
    if (delta < 0) return { label: `↓ ${value} %`, className: "text-color-danger" }
    return { label: "→ 0 %", className: "text-[#5F666C]" }
}

type ExtendedFieldsAvailability = "available" | "partial" | "unavailable"

const getExtendedFieldsAvailability = (
    period: { from: string; to: string },
    extendedFieldsSince: string | null
): ExtendedFieldsAvailability => {
    if (extendedFieldsSince == null) return "unavailable"

    const periodStart = Date.parse(`${period.from}T00:00:00.000Z`)
    const inclusivePeriodEnd = Date.parse(`${period.to}T00:00:00.000Z`)
    const periodEnd = inclusivePeriodEnd + 24 * 60 * 60 * 1000
    const boundary = Date.parse(extendedFieldsSince)
    if ([periodStart, periodEnd, boundary].some(Number.isNaN)) return "unavailable"
    if (periodStart >= boundary) return "available"
    if (periodEnd <= boundary) return "unavailable"

    return "partial"
}

const buildBucketPeriod = (
    bucketStart: string,
    period: PeriodValue,
    interval: "day" | "week" | "month"
): PeriodValue => {
    const rawFrom = parseISO(bucketStart)
    const rawTo = interval === "week" ? addDays(rawFrom, 6) : interval === "month" ? endOfMonth(rawFrom) : rawFrom
    const periodFrom = parseISO(period.from)
    const periodTo = parseISO(period.to)
    const from = rawFrom.getTime() < periodFrom.getTime() ? periodFrom : rawFrom
    const to = rawTo.getTime() > periodTo.getTime() ? periodTo : rawTo

    return {
        preset: "custom",
        from: format(from, "yyyy-MM-dd"),
        to: format(to, "yyyy-MM-dd"),
        interval: period.interval,
    }
}

interface MetricProps {
    icon: FC<SVGProps<SVGSVGElement>>
    label: string
    value: string
    delta: { label: string; className: string }
    onClick: () => void
}

const Metric = ({ icon, label, value, delta, onClick }: MetricProps) => (
    <button type="button" onClick={onClick} className="box w-full text-left transition hover:shadow-md">
        <StatisticsBlock icon={icon} label={label} value={value} />
        <div className={`mt-3 text-sm font-semibold ${delta.className}`}>
            {delta.label} к предыдущему периоду
        </div>
    </button>
)

const RetryState = ({ message, onRetry }: { message: string; onRetry: () => void }) => (
    <div className="info_panel flex items-center justify-between gap-4">
        <span className="text-color-danger">{message}</span>
        <button type="button" onClick={onRetry} className="form_handle_button !w-auto px-6">Повторить</button>
    </div>
)

export const LoyaltyStatisticsPage = () => {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const filters = useAppSelector(selectLoyaltyAnalyticsFilters)
    const appliedFiltersCount = useAppSelector(selectLoyaltyStatisticsAppliedFiltersCount)
    const isPeriodTooLong = isLoyaltyAnalyticsPeriodTooLong(filters.period)
    const summaryRequestId = useRef(0)
    const chartRequestId = useRef(0)
    const levelsRequestId = useRef(0)
    const [summaryStatus, setSummaryStatus] = useState<LoadStatus>("idle")
    const [summary, setSummary] = useState<ILoyaltySummaryResponse | null>(null)
    const [previousSummary, setPreviousSummary] = useState<ILoyaltySummaryResponse | null>(null)
    const [previousStatus, setPreviousStatus] = useState<LoadStatus>("idle")
    const [summaryError, setSummaryError] = useState("")
    const [chartStatus, setChartStatus] = useState<LoadStatus>("idle")
    const [timeSeries, setTimeSeries] = useState<ILoyaltyTimeSeriesResponse | null>(null)
    const [chartError, setChartError] = useState("")
    const [levelsStatus, setLevelsStatus] = useState<LoadStatus>("idle")
    const [levelBreakdown, setLevelBreakdown] = useState<ILoyaltyLevelBreakdownResponse | null>(null)
    const [levelsError, setLevelsError] = useState("")

    const fetchSummaries = useCallback(() => {
        const requestId = ++summaryRequestId.current
        if (isPeriodTooLong) {
            setSummaryStatus("idle")
            setSummary(null)
            setSummaryError("")
            setPreviousStatus("idle")
            setPreviousSummary(null)
            return
        }
        const previousPeriod = getPreviousPeriod(filters.period)
        const common = {
            levels: filters.levels,
            includeUnknownLevel: filters.includeUnknownLevel,
        }
        setSummaryStatus("loading")
        setSummary(null)
        setSummaryError("")
        setPreviousStatus("loading")
        setPreviousSummary(null)

        getLoyaltySummary({ ...common, from: filters.period.from, to: filters.period.to })
            .then((response) => {
                if (summaryRequestId.current !== requestId) return
                setSummary(response.data)
                setSummaryStatus("loaded")
            })
            .catch((error) => {
                if (summaryRequestId.current !== requestId) return
                setSummaryStatus("error")
                setSummaryError(getLoyaltyAnalyticsErrorMessage(error))
            })

        getLoyaltySummary({ ...common, from: previousPeriod.from, to: previousPeriod.to })
            .then((response) => {
                if (summaryRequestId.current !== requestId) return
                setPreviousSummary(response.data)
                setPreviousStatus("loaded")
            })
            .catch(() => {
                if (summaryRequestId.current !== requestId) return
                setPreviousSummary(null)
                setPreviousStatus("error")
            })
    }, [filters.includeUnknownLevel, filters.levels, filters.period, isPeriodTooLong])

    const fetchChart = useCallback(() => {
        const requestId = ++chartRequestId.current
        if (isPeriodTooLong) {
            setChartStatus("idle")
            setTimeSeries(null)
            setChartError("")
            return
        }
        setChartStatus("loading")
        setTimeSeries(null)
        setChartError("")

        getLoyaltyTimeSeries({
            from: filters.period.from,
            to: filters.period.to,
            levels: filters.levels,
            includeUnknownLevel: filters.includeUnknownLevel,
            interval: filters.period.interval,
        }).then((response) => {
            if (chartRequestId.current !== requestId) return
            setTimeSeries(response.data)
            setChartStatus("loaded")
        }).catch((error) => {
            if (chartRequestId.current !== requestId) return
            setChartStatus("error")
            setChartError(getLoyaltyAnalyticsErrorMessage(error))
        })
    }, [filters.includeUnknownLevel, filters.levels, filters.period, isPeriodTooLong])

    const fetchLevels = useCallback(() => {
        const requestId = ++levelsRequestId.current
        if (isPeriodTooLong) {
            setLevelsStatus("idle")
            setLevelBreakdown(null)
            setLevelsError("")
            return
        }
        setLevelsStatus("loading")
        setLevelBreakdown(null)
        setLevelsError("")

        getLoyaltyLevelBreakdown({ from: filters.period.from, to: filters.period.to })
            .then((response) => {
                if (levelsRequestId.current !== requestId) return
                setLevelBreakdown(response.data)
                setLevelsStatus("loaded")
            })
            .catch((error) => {
                if (levelsRequestId.current !== requestId) return
                setLevelsStatus("error")
                setLevelsError(getLoyaltyAnalyticsErrorMessage(error))
            })
    }, [filters.period.from, filters.period.to, isPeriodTooLong])

    useEffect(() => fetchSummaries(), [fetchSummaries])
    useEffect(() => fetchChart(), [fetchChart])
    useEffect(() => fetchLevels(), [fetchLevels])

    const levelOptions = useMemo(() => {
        const rows = levelBreakdown?.rows ?? []
        const loadedLevels = new Set(rows.map((row) => String(row.level)))
        return [
            { value: ALL_OPTION_VALUE, label: "Все уровни" },
            ...rows.map((row) => ({
                value: String(row.level),
                label: `${row.level} — ${row.levelName}${row.isDeletedLevel ? " (удалён)" : ""}`,
            })),
            ...filters.levels
                .filter((level) => !loadedLevels.has(String(level)))
                .map((level) => ({ value: String(level), label: `Уровень ${level}` })),
            ...(Number(levelBreakdown?.unknownLevelAccrualsCount ?? 0) > 0 || filters.includeUnknownLevel
                ? [{ value: UNKNOWN_LEVEL_OPTION_VALUE, label: "Уровень не указан (н/д)" }]
                : []),
        ]
    }, [filters.includeUnknownLevel, filters.levels, levelBreakdown])

    const selectedLevelOptions = levelOptions.filter((option) =>
        (option.value === UNKNOWN_LEVEL_OPTION_VALUE && filters.includeUnknownLevel)
        || (
            option.value !== ALL_OPTION_VALUE
            && option.value !== UNKNOWN_LEVEL_OPTION_VALUE
            && filters.levels.includes(Number(option.value))
        )
    )

    const drillDown = useCallback((period: PeriodValue) => {
        dispatch(applyDrilldown({
            period,
            levels: filters.levels,
            includeUnknownLevel: filters.includeUnknownLevel,
        }))
        navigate("/loyalty-levels/accruals")
    }, [dispatch, filters.includeUnknownLevel, filters.levels, navigate])

    const chartPoints = useMemo(
        () => timeSeries?.points.filter((point) => point.bucketStart !== null) ?? [],
        [timeSeries]
    )
    const chartData: ChartData<"bar"> = useMemo(() => ({
        labels: chartPoints.map((point) => new Intl.DateTimeFormat("ru-RU", {
            day: "2-digit",
            month: "short",
            year: "numeric",
        }).format(parseISO(point.bucketStart as string))),
        datasets: [
            {
                label: "Начисления",
                data: chartPoints.map((point) => Number(point.accrualsCount)),
                backgroundColor: "rgba(5, 205, 153, 0.75)",
                borderColor: "#05CD99",
                borderWidth: 1,
                borderRadius: 6,
                yAxisID: "count",
            },
            {
                label: "Кэшбэк, ₽",
                data: chartPoints.map((point) => Number(point.cashbackAmount)),
                backgroundColor: "rgba(22, 47, 43, 0.72)",
                borderColor: "#162F2B",
                borderWidth: 1,
                borderRadius: 6,
                yAxisID: "money",
            },
        ],
    }), [chartPoints])

    const chartOptions: ChartOptions<"bar"> = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        interaction: { intersect: false, mode: "index" },
        plugins: { legend: { display: true, position: "bottom" } },
        scales: {
            count: { beginAtZero: true, position: "left", ticks: { precision: 0 } },
            money: {
                beginAtZero: true,
                position: "right",
                grid: { drawOnChartArea: false },
                ticks: {
                    callback: (value) =>
                        `${new Intl.NumberFormat("ru-RU", { notation: "compact" }).format(Number(value))} ₽`,
                },
            },
        },
        onClick: (_event, elements) => {
            const point = chartPoints[elements[0]?.index]
            if (!point?.bucketStart) return
            drillDown(buildBucketPeriod(point.bucketStart, filters.period, filters.period.interval))
        },
    }), [chartPoints, drillDown, filters.period])

    const comparablePrevious = previousStatus === "loaded"
        && !summary?.truncated
        && !previousSummary?.truncated
        ? previousSummary
        : null
    const previousPeriod = getPreviousPeriod(filters.period)
    const extendedFieldsAvailability = summary
        ? getExtendedFieldsAvailability(filters.period, summary.extendedFieldsSince)
        : "available"
    const previousExtendedFieldsAvailability = comparablePrevious
        ? getExtendedFieldsAvailability(previousPeriod, comparablePrevious.extendedFieldsSince)
        : "available"
    const currentFiat = summary && extendedFieldsAvailability !== "unavailable"
        ? summary.totalPaidAmountFiat
        : null
    const currentBonus = summary && extendedFieldsAvailability !== "unavailable"
        ? summary.totalPaidAmountBonus
        : null
    const canCompareExtendedFields = extendedFieldsAvailability === "available"
        && previousExtendedFieldsAvailability === "available"
    const currentFiatForDelta = canCompareExtendedFields ? currentFiat : null
    const currentBonusForDelta = canCompareExtendedFields ? currentBonus : null
    const previousFiatForDelta = canCompareExtendedFields ? comparablePrevious?.totalPaidAmountFiat : null
    const previousBonusForDelta = canCompareExtendedFields ? comparablePrevious?.totalPaidAmountBonus : null
    const showExtendedFootnote = summary && extendedFieldsAvailability !== "available"

    return (
        <div className="_page_container">
            <PageHeader title="Статистика уровней лояльности" />
            <div className="flexbox">
                <div className="box">
                    <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
                        <h2 className="text-lg font-bold screen:text-2xl">Период и фильтры</h2>
                        <div className="flex items-center gap-3">
                            <span className="text-sm text-[#5F666C]">Применено фильтров: {appliedFiltersCount}</span>
                            <button
                                type="button"
                                onClick={() => dispatch(resetFilters())}
                                className="form_cancel_button !h-10 !w-auto px-5"
                            >
                                Сбросить
                            </button>
                        </div>
                    </div>
                    <PeriodPicker
                        value={filters.period}
                        onChange={(period) => dispatch(setPeriod(period))}
                        withInterval
                    />
                    {isPeriodTooLong && (
                        <div className="mt-3 text-color-danger">
                            Максимальный период — {MAX_PERIOD_DAYS} дней. Сузьте диапазон.
                        </div>
                    )}
                    <div className="mt-4">
                        <Select
                            isMulti
                            isLoading={levelsStatus === "loading"}
                            options={levelOptions}
                            value={selectedLevelOptions}
                            placeholder="Все уровни"
                            onChange={(options) => {
                                const values = (options || []).map((option: { value: string }) => option.value)
                                if (values.length === 0 || values.includes(ALL_OPTION_VALUE)) {
                                    dispatch(setLevels([]))
                                    dispatch(setIncludeUnknownLevel(false))
                                    return
                                }
                                dispatch(setLevels(values
                                    .filter((value: string) => value !== UNKNOWN_LEVEL_OPTION_VALUE)
                                    .map(Number)))
                                dispatch(setIncludeUnknownLevel(values.includes(UNKNOWN_LEVEL_OPTION_VALUE)))
                            }}
                        />
                    </div>
                    {levelsStatus === "error" && (
                        <div className="mt-3"><RetryState message={levelsError} onRetry={fetchLevels} /></div>
                    )}
                    {levelsStatus === "loaded" && levelBreakdown?.rows.length === 0 && (
                        <div className="mt-3 text-sm text-[#828282]">нет активных уровней за период</div>
                    )}
                    {levelBreakdown?.truncated && (
                        <div className="mt-3 rounded-xl bg-[#FFF4D6] p-3 text-[#8A5B00]">
                            Список уровней усечён серверным лимитом
                        </div>
                    )}
                </div>

                {summaryStatus === "loading" && <div className="info_panel flex justify-center"><Loader /></div>}
                {summaryStatus === "error" && <RetryState message={summaryError} onRetry={fetchSummaries} />}
                {summaryStatus === "loaded" && summary?.truncated && (
                    <div className="rounded-xl bg-[#FFF4D6] p-4 text-[#8A5B00]">
                        Сводные показатели усечены серверным лимитом периода
                    </div>
                )}
                {summaryStatus === "loaded" && Number(summary?.accrualsCount) === 0 && (
                    <NoTableData label="нет данных за период" />
                )}
                {summaryStatus === "loaded" && summary && Number(summary.accrualsCount) > 0 && (
                    <>
                        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
                            <Metric
                                icon={CoinsSvg}
                                label={filters.levels.length === 0 && !filters.includeUnknownLevel
                                    ? "Кэшбэк (включая удалённые уровни)"
                                    : "Кэшбэк за выбранные уровни"}
                                value={formatLoyaltyMoney(summary.totalCashbackAmount)}
                                delta={getDelta(summary.totalCashbackAmount, comparablePrevious?.totalCashbackAmount)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <Metric
                                icon={MoneySvg}
                                label="Сумма покупок — фиат"
                                value={formatLoyaltyMoney(currentFiat)}
                                delta={getDelta(currentFiatForDelta, previousFiatForDelta)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <Metric
                                icon={WalletSvg}
                                label="Сумма покупок — бонусы"
                                value={formatLoyaltyMoney(currentBonus)}
                                delta={getDelta(currentBonusForDelta, previousBonusForDelta)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <Metric
                                icon={PercentSvg}
                                label="Число начислений"
                                value={formatLoyaltyNumber(summary.accrualsCount)}
                                delta={getDelta(summary.accrualsCount, comparablePrevious?.accrualsCount)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <Metric
                                icon={UsersFourSvg}
                                label="Уникальные клиенты"
                                value={formatLoyaltyNumber(summary.uniqueClientsCount)}
                                delta={getDelta(summary.uniqueClientsCount, comparablePrevious?.uniqueClientsCount)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <Metric
                                icon={BagSvg}
                                label="Средний чек"
                                value={formatLoyaltyMoney(summary.averagePurchase)}
                                delta={getDelta(summary.averagePurchase, comparablePrevious?.averagePurchase)}
                                onClick={() => drillDown(filters.period)}
                            />
                            <Metric
                                icon={UserListSvg}
                                label="Средний кэшбэк"
                                value={formatLoyaltyMoney(summary.averageCashback)}
                                delta={getDelta(summary.averageCashback, comparablePrevious?.averageCashback)}
                                onClick={() => drillDown(filters.period)}
                            />
                        </div>
                        {showExtendedFootnote && (
                            <div className="text-sm text-[#5F666C]">
                                {summary.extendedFieldsSince == null
                                    ? "н/д — раздельный учёт фиат/бонусы недоступен за выбранный период"
                                    : <>
                                        {extendedFieldsAvailability === "unavailable" && "н/д — "}
                                        данные доступны с {formatExtendedFieldsSince(summary.extendedFieldsSince)}
                                    </>}
                            </div>
                        )}
                    </>
                )}

                {chartStatus === "loading" && <div className="info_panel flex justify-center"><Loader /></div>}
                {chartStatus === "error" && <RetryState message={chartError} onRetry={fetchChart} />}
                {chartStatus === "loaded" && timeSeries && (
                    <>
                        {timeSeries.truncated && (
                            <div className="rounded-xl bg-[#FFF4D6] p-4 text-[#8A5B00]">
                                График усечён серверным лимитом периода
                            </div>
                        )}
                        <ChartBlock
                            type="bar"
                            title="Динамика начислений и кэшбэка"
                            chartClass="h-[380px]"
                            data={chartData}
                            options={chartOptions}
                            noData={chartPoints.length === 0}
                            noDataComponent={<NoTableData label="нет данных за период" />}
                        />
                    </>
                )}
            </div>
        </div>
    )
}
